// ANCHOR React
import { useEffect, useState } from 'react';

// ANCHOR Toast
import { toast } from 'react-hot-toast';

// ANCHOR Models
import createModel, { createSelectors } from 'react-scoped-model';
import { useFormQuestionActiveValue } from '@lyon/scoped-models/form/FormQuestionActive';
import { useFormQuestionMutate } from '@lyon/scoped-models/form/FormQuestionData';
import { useFormQuestionsMutate } from '@lyon/scoped-models/form/FormQuestionsData';

// ANCHOR Graph State
import { useAuthToken } from '@lyon/nodes/firebase/auth/auth-token-node';

// ANCHOR Hooks
import { useSentryExceptionHandler } from '@lyon/utils/hooks/useSentryExceptionHandler';
import { usePromise } from '@lyon/utils/hooks/usePromise';

// ANCHOR API
import { updateFormQuestionById } from '@lyon/utils/api/forms';

// ANCHOR Utils
import { tuple } from '@lyon/utils/tuple';
import { IFormQuestionsResult } from '@lyon/utils/api/results/forms';

interface IProps {
  initialData: IFormQuestionsResult;
}

export const UpdateFormQuestionForm = createModel(({
  initialData,
}: IProps) => {
  const [name, setName] = useState(initialData.name);
  const [type, setType] = useState(initialData.type);
  const [position, setPosition] = useState(initialData.position);
  const [caption, setCaption] = useState(initialData.caption);
  const [isOpenEnded, setIsOpenEnded] = useState(initialData.isOpenEnded);
  const [
    pointsPossible, setPointsPossible,
  ] = useState<number | undefined>(initialData.pointsPossible);
  const [
    answerCount, setAnswerCount,
  ] = useState<number | undefined>(initialData.answerCount);
  const [isCaseSensitive, setIsCaseSensitive] = useState(initialData.isCaseSensitive);

  // NOTE useFreshState causes the input fields to not update on value change
  useEffect(() => {
    setName(initialData.name);
    setType(initialData.type);
    setPosition(initialData.position);
    setCaption(initialData.caption);
    setIsOpenEnded(initialData.isOpenEnded);
    setPointsPossible(initialData.pointsPossible);
    setAnswerCount(initialData.answerCount);
    setIsCaseSensitive(initialData.isCaseSensitive);
  }, [initialData]);

  const token = useAuthToken();
  const catchException = useSentryExceptionHandler();
  const mutate = useFormQuestionMutate();
  const mutateSidebar = useFormQuestionsMutate();
  const activeQuestionId = useFormQuestionActiveValue();

  const mounted = usePromise([
    token, name, type, position, caption, isOpenEnded, pointsPossible,
    isCaseSensitive, activeQuestionId,
  ]);

  const isDisabled = !name || !type;
  const [loading, setLoading] = useState(false);

  function submit() {
    if (token && activeQuestionId && name && type) {
      setLoading(true);

      const request = (async () => {
        await mounted(updateFormQuestionById({
          token,
          id: activeQuestionId,
          name,
          type,
          position,
          caption,
          isOpenEnded,
          pointsPossible: pointsPossible ?? 0,
          answerCount,
          isCaseSensitive,
        }));

        await mounted(mutateSidebar());
        await mounted(mutate());
      })();

      toast.promise(request, {
        loading: 'Updating form content',
        success: 'Successfully updated form content',
        error: 'Failed updating form content',
      }).then(() => {
        setLoading(false);
      }, (error) => {
        setLoading(false);
        catchException(error);
      });
    }
  }

  return {
    state: {
      name,
      type,
      position,
      caption,
      isOpenEnded,
      pointsPossible,
      answerCount,
      isCaseSensitive,
      isDisabled,
      loading,
    },
    handler: {
      name: setName,
      type: setType,
      position: setPosition,
      caption: setCaption,
      isOpenEnded: setIsOpenEnded,
      pointsPossible: setPointsPossible,
      answerCount: setAnswerCount,
      isCaseSensitive: setIsCaseSensitive,
      submit,
    },
  };
}, {
  displayName: 'Models.UpdateFormQuestionForm',
});

export const useUpdateFormQuestionName = createSelectors(
  UpdateFormQuestionForm,
  ({ state, handler }) => tuple(
    state.name,
    handler.name,
  ),
);

export const useUpdateFormQuestionCaption = createSelectors(
  UpdateFormQuestionForm,
  ({ state, handler }) => tuple(
    state.caption,
    handler.caption,
  ),
);

export const useUpdateFormQuestionPointsPossible = createSelectors(
  UpdateFormQuestionForm,
  ({ state, handler }) => tuple(
    state.pointsPossible,
    handler.pointsPossible,
    handler.submit,
  ),
);

export const useUpdateFormQuestionAnswerCount = createSelectors(
  UpdateFormQuestionForm,
  ({ state, handler }) => tuple(
    state.answerCount,
    handler.answerCount,
  ),
);

export const useUpdateFormQuestionIsCaseSensitive = createSelectors(
  UpdateFormQuestionForm,
  ({ state, handler }) => tuple(
    state.isCaseSensitive,
    handler.isCaseSensitive,
  ),
);

export const useUpdateFormQuestionFormSubmitPayload = createSelectors(
  UpdateFormQuestionForm,
  ({ state, handler }) => tuple(
    handler.submit,
    state.isDisabled,
    state.loading,
  ),
);
